import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Download, CheckCircle2, FileText } from "lucide-react";
import { PeakMark } from "@/components/PeakMark";
import { useSeo } from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const insides = [
  "A 5-week structured training plan",
  "Foundation, Specificity & Taper phases",
  "Running, strength & compromised-running work",
  "Station strategy & smooth transitions",
  "Race-week fueling and rest",
  "Race-day pacing & the mistakes to avoid",
];

export default function HyroxGuidePage() {
  useSeo({
    title: "The HYROX Race Prep Guide (Free) | Smith Strategic Group",
    description:
      "A free 5-week HYROX race prep guide — build the engine, strength, and race-day strategy to show up prepared for every station.",
    path: "/hyrox-race-prep-guide",
    noindex: true,
  });

  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        source: "hyrox_guide_landing_page",
        resource: "hyrox_race_prep",
      });
      setResult(res.data);
      if (res.data?.download_url) {
        window.open(res.data.download_url, "_blank", "noopener,noreferrer");
      }
      toast.success("Your guide is opening in a new tab.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body antialiased ssg-grain relative overflow-hidden">
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-52 -left-40 w-[560px] h-[560px] rounded-full opacity-15 blur-3xl"
        style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
      />

      {/* Minimal header — logo only, links home */}
      <header className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center">
        <Link to="/" data-testid="hyrox-lp-logo" className="flex items-center gap-3" aria-label="Smith Strategic Group">
          <span className="inline-flex items-center justify-center w-10 h-10 bg-black overflow-hidden border border-white/10">
            <img
              src="https://customer-assets.emergentagent.com/job_ssg-coaching/artifacts/pkgt7i93_489F0D9A-2A77-47CF-B54B-18E9E2CA80E0.png"
              alt="SSG"
              className="w-10 h-10 object-contain"
            />
          </span>
          <span className="font-display tracking-tight text-[16px] font-bold leading-none">
            Smith Strategic Group
          </span>
        </Link>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-14 md:py-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* LEFT — copy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-4 mb-8">
            <PeakMark size={30} className="text-white/80" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
              Free Guide · 7 Pages
            </span>
          </div>
          <h1
            data-testid="hyrox-lp-headline"
            className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.0]"
          >
            The HYROX<br />
            <span className="italic font-light">Race Prep Guide</span>
            <span className="text-[#E25555]">.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-neutral-300 leading-relaxed font-medium max-w-xl">
            Five weeks to a stronger start line. Build the engine, the strength,
            and the race-day strategy to show up prepared for every station —
            not just hoping to survive it.
          </p>

          <div className="mt-10">
            <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-neutral-500 mb-5">
              Inside the guide
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {insides.map((item, i) => (
                <li
                  key={item}
                  data-testid={`hyrox-lp-inside-${i}`}
                  className="flex items-start gap-3 text-[15px] text-neutral-200 font-medium"
                >
                  <CheckCircle2 size={16} strokeWidth={1.7} className="text-[#E25555] mt-0.5 flex-none" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* RIGHT — opt-in card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="bg-[#141414] border border-white/10 p-8 md:p-10 shadow-2xl">
            {result ? (
              <div data-testid="hyrox-lp-success">
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#E25555] mb-4">
                  <CheckCircle2 size={16} strokeWidth={1.8} />
                  Delivered
                </div>
                <h2 className="font-display text-3xl font-black tracking-tight">
                  It&apos;s opening now.
                </h2>
                <p className="mt-4 text-[14px] text-neutral-400 leading-relaxed">
                  Your guide should be opening in a new tab. If your browser
                  blocked it, use the button below.
                </p>
                <a
                  href={result.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hyrox-lp-download-again"
                  className="mt-7 inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-7 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                >
                  <Download size={16} strokeWidth={1.6} />
                  Download the PDF
                </a>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="hyrox-lp-form">
                <div className="flex items-center gap-2 mb-3 text-[10px] tracking-[0.22em] uppercase font-bold text-[#E25555]">
                  <FileText size={13} strokeWidth={1.8} />
                  Get instant access
                </div>
                <h2 className="font-display text-2xl md:text-[26px] font-black tracking-tight leading-tight">
                  Send me the guide.
                </h2>
                <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">
                  Free download. No spam — just the guide, straight to your
                  inbox and open on-screen.
                </p>

                <div className="mt-6 space-y-5">
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    required
                    placeholder="Your name"
                    data-testid="hyrox-lp-input-name"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E25555] focus:ring-0 outline-none px-0 py-3 text-base text-white placeholder:text-neutral-500 transition-colors"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    placeholder="you@email.com"
                    data-testid="hyrox-lp-input-email"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E25555] focus:ring-0 outline-none px-0 py-3 text-base text-white placeholder:text-neutral-500 transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="hyrox-lp-submit"
                  className="group mt-7 w-full inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white disabled:opacity-60 disabled:cursor-not-allowed px-6 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                >
                  {loading ? "Sending…" : "Send Me The Guide"}
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.6}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
                <p className="mt-4 text-[10px] tracking-[0.18em] uppercase font-bold text-neutral-500 text-center">
                  Instant PDF download
                </p>
              </form>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3 justify-center">
            <PeakMark size={16} className="text-white/40" />
            <Link
              to="/performance"
              data-testid="hyrox-lp-perf-link"
              className="text-[10px] tracking-[0.24em] uppercase font-bold text-neutral-500 hover:text-white transition-colors"
            >
              Explore SSG Performance Coaching
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
